import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Flex, Spin, Typography } from 'antd';
import { CustomModalWithoutButton, CustomButton } from '../../components';
import {
  GetWorldCourierBookingById,
  PromoteShipment,
} from '../../apis/shipment';

const { Text } = Typography;

const POLL_MAX_MS = 180_000;
const POLL_INTERVAL_MS = 2_000;

export interface WorldCourierBookingCompletedModalProps {
  open: boolean;
  onClose: () => void;
  bookingId: string | null;
  token: string;
  shellShipmentId?: string;
  courierId?: string;
  onPromoteSuccess?: () => void;
}

const WorldCourierBookingCompletedModal: React.FC<
  WorldCourierBookingCompletedModalProps
> = ({
  open,
  onClose,
  bookingId,
  token,
  shellShipmentId,
  courierId,
  onPromoteSuccess,
}) => {
  const [hwbNumber, setHwbNumber] = useState<string | null>(null);
  const [bookingTerminalStatus, setBookingTerminalStatus] = useState<
    'submitted' | 'failed' | 'cancelled' | null
  >(null);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [promoteError, setPromoteError] = useState<string | null>(null);
  const promoteAttemptedRef = useRef<string | null>(null);
  const promoteSuccessNotifiedRef = useRef(false);
  const onPromoteSuccessRef = useRef(onPromoteSuccess);

  useEffect(() => {
    onPromoteSuccessRef.current = onPromoteSuccess;
  }, [onPromoteSuccess]);

  const setModalOpenBridge = useCallback((_next: boolean) => {}, []);

  const shellPromoteEnabled = Boolean(shellShipmentId && courierId);

  useEffect(() => {
    if (!open || !bookingId || !token) return;

    setHwbNumber(null);
    setBookingTerminalStatus(null);
    setPollTimedOut(false);
    setFetchFailed(false);
    setWaiting(false);
    setPromoteError(null);
    promoteAttemptedRef.current = null;
    promoteSuccessNotifiedRef.current = false;

    let cancelled = false;
    let timer: number | null = null;
    const startedAt = Date.now();

    function clearPollTimer(): void {
      if (timer != null) {
        window.clearTimeout(timer);
        timer = null;
      }
    }

    function scheduleNext(): void {
      if (cancelled) return;
      clearPollTimer();
      timer = window.setTimeout(() => {
        void tick();
      }, POLL_INTERVAL_MS);
    }

    async function tick(): Promise<void> {
      if (cancelled) {
        setWaiting(false);
        return;
      }
      setWaiting(true);
      try {
        const res = await GetWorldCourierBookingById(token, bookingId!);
        if (cancelled) {
          setWaiting(false);
          return;
        }
        setFetchFailed(false);
        const wrap = res?.data?.data as
          | {
              data?: {
                booking_status?: string | null;
                wc_portal_order_number?: string | null;
              };
              booking_status?: string | null;
              wc_portal_order_number?: string | null;
            }
          | undefined;
        const nested = wrap?.data;
        const status: string = (nested?.booking_status ??
          wrap?.booking_status ??
          '') as string;
        const orderNumber: string | null = (nested?.wc_portal_order_number ??
          wrap?.wc_portal_order_number ??
          null) as string | null;

        if (status === 'failed') {
          setBookingTerminalStatus('failed');
          setWaiting(false);
          clearPollTimer();
          return;
        }
        if (status === 'cancelled') {
          setBookingTerminalStatus('cancelled');
          setWaiting(false);
          clearPollTimer();
          return;
        }
        if (status === 'submitted') {
          setBookingTerminalStatus('submitted');
          setHwbNumber(orderNumber);
          setWaiting(false);
          clearPollTimer();
          return;
        }
      } catch {
        if (!cancelled) setFetchFailed(true);
      }

      if (cancelled) {
        setWaiting(false);
        return;
      }
      if (Date.now() - startedAt >= POLL_MAX_MS) {
        setPollTimedOut(true);
        setWaiting(false);
        return;
      }
      scheduleNext();
    }

    void tick();

    return () => {
      cancelled = true;
      clearPollTimer();
    };
  }, [open, bookingId, token]);

  useEffect(() => {
    if (!shellPromoteEnabled || !shellShipmentId || !courierId || !token) {
      return;
    }
    if (bookingTerminalStatus !== 'submitted' || !hwbNumber?.trim()) {
      return;
    }
    const promoteKey = `${shellShipmentId}:${hwbNumber.trim()}`;
    if (promoteAttemptedRef.current === promoteKey) return;
    promoteAttemptedRef.current = promoteKey;

    let cancelled = false;

    void (async () => {
      try {
        await PromoteShipment(token, shellShipmentId, {
          courier_id: courierId,
          courier_tracking_id: hwbNumber.trim(),
        });
        if (!promoteSuccessNotifiedRef.current) {
          promoteSuccessNotifiedRef.current = true;
          onPromoteSuccessRef.current?.();
        }
      } catch (err: unknown) {
        const status = (err as { status?: number })?.status;
        if (status === 409) {
          if (!promoteSuccessNotifiedRef.current) {
            promoteSuccessNotifiedRef.current = true;
            onPromoteSuccessRef.current?.();
          }
          return;
        }
        if (!cancelled) {
          setPromoteError(
            'Could not link this booking to the shipment — enter tracking manually on the shipment.'
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    shellPromoteEnabled,
    shellShipmentId,
    courierId,
    token,
    bookingTerminalStatus,
    hwbNumber,
  ]);

  return (
    <CustomModalWithoutButton
      modalOpen={open}
      setModalOpen={setModalOpenBridge}
      modalTitle='World Courier booking completed'
      lineHeight='28px'
      fontSize='20px'
      modalSize={600}
      zIndex={1200}
      maskClosable={false}
      closable={false}
      keyboard={false}
    >
      <Flex vertical gap={0} style={{ width: '100%' }}>
        <Typography.Paragraph style={{ marginBottom: 12, marginTop: 0 }}>
          Your HWB #{' '}
          {bookingTerminalStatus === 'submitted' ? (
            hwbNumber ? (
              <>
                is <Text strong>{hwbNumber}</Text>
              </>
            ) : (
              <Text type='secondary'>
                was not captured — booking submitted successfully. Check the
                booking record for the HWB number.
              </Text>
            )
          ) : bookingTerminalStatus === 'failed' ? (
            <Text type='secondary'>
              is unavailable — automation reported a failure (see below).
            </Text>
          ) : bookingTerminalStatus === 'cancelled' ? (
            <Text type='secondary'>
              is not applicable — this booking was cancelled.
            </Text>
          ) : pollTimedOut ? (
            <Text type='secondary'>
              is not available yet — check the booking record once the portal
              submission completes.
            </Text>
          ) : (
            <span>
              <Spin size='small' />{' '}
              <Text type='secondary'>Waiting for portal submission…</Text>
            </span>
          )}
        </Typography.Paragraph>

        {shellShipmentId && !courierId ? (
          <Alert
            type='warning'
            showIcon
            message='Could not resolve courier for linking — enter tracking manually on the shipment.'
            style={{ marginBottom: 12 }}
          />
        ) : null}

        {promoteError ? (
          <Alert
            type='error'
            showIcon
            message={promoteError}
            style={{ marginBottom: 12 }}
          />
        ) : null}

        {fetchFailed ? (
          <Alert
            type='warning'
            showIcon
            message='Could not refresh booking details. Retrying…'
            style={{ marginBottom: 12 }}
          />
        ) : null}

        {bookingTerminalStatus === 'failed' ? (
          <Alert
            type='error'
            showIcon
            message='Booking automation reported a failure. The World Courier portal submission may not have completed — check the booking record or retry from Operations.'
            style={{ marginBottom: 12 }}
          />
        ) : null}

        {bookingTerminalStatus === 'cancelled' ? (
          <Alert
            type='warning'
            showIcon
            message='This booking was cancelled. Polling has stopped.'
            style={{ marginBottom: 12 }}
          />
        ) : null}

        {pollTimedOut && !bookingTerminalStatus ? (
          <Alert
            type='info'
            showIcon
            message='Portal submission is still pending. Check back on the booking record.'
            style={{ marginBottom: 12 }}
          />
        ) : null}

        {waiting && !bookingTerminalStatus && !pollTimedOut ? (
          <Text type='secondary' style={{ display: 'block', marginBottom: 8 }}>
            Waiting for World Courier portal submission to complete… (up to a
            few minutes)
          </Text>
        ) : null}

        <Flex justify='flex-end' style={{ marginTop: 20 }}>
          <CustomButton
            text='Done'
            bgColor='#000'
            color='#fff'
            fontSize='14px'
            fontWeight='600'
            height='36px'
            borderRadius='8px'
            padding='0 32px'
            onClick={onClose}
          />
        </Flex>
      </Flex>
    </CustomModalWithoutButton>
  );
};

export default WorldCourierBookingCompletedModal;
